import { NumberInput } from '@frontend/basic-forms';
import { ICONS } from '@frontend/icons';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { RequiredFlag } from '../required-flag';
import { DurationInputSelect } from './duration-input-select';
import useDurationInput from './duration-input.controller';

export interface DurationInputProps {
    id?: string;
    required?: boolean;
    className?: string;
    duration?: number;
    timeUnit?: string;
    timePeriod?: string;
    onApplyFilter: (
        duration: number | undefined,
        unit: string | undefined,
        period: string | undefined,
        payload: { key: string; value: string | undefined }
    ) => void;
    label?: React.ReactNode;
    helpMessage?: React.ReactNode;
    hideLabel?: boolean;
    allowURLChange?: boolean;
}

export const DurationInput = (props: DurationInputProps) => {
    const viewProps = useDurationInput(props);

    return (
        <div
            ref={viewProps.dropdownRef}
            className={`form-group ${props.className ? props.className : 'w-full'}`}>
            <div className='mb-0! flex w-full flex-col'>
                {!props.hideLabel && (
                    <div className='inline-flex items-center gap-1'>
                        <span className='text-foreground-default text-sm font-medium'>{props.label}</span>
                        <RequiredFlag required={props.required} />
                    </div>
                )}
                {props.helpMessage && (
                    <>
                        <small className='form-text text-brand-muted'>{props.helpMessage}</small>
                    </>
                )}
            </div>
            <div className='outline-border-default bg-background-surface inline-flex h-10 w-full min-w-[120px] flex-nowrap items-center justify-start gap-2 overflow-hidden rounded-xl px-4 font-medium outline-1 outline-offset-[-1px]'>
                <button
                    className='text-brand-muted w-full flex-1 cursor-pointer py-4 text-start'
                    onClick={() => viewProps.changeShowModal(!viewProps.showModal)}>
                    {viewProps.displayText ? viewProps.displayText : 'Select...'}
                </button>
                {viewProps.duration && viewProps.timeUnit && viewProps.timePeriod && (
                    <ICONS.BUTTON.CLOSE
                        className='text-brand-muted hover:text-foreground-default hover:cursor-pointer'
                        onClick={() => {
                            let formatDate = viewProps.formatToDate();
                            formatDate.value = undefined;
                            props.onApplyFilter(undefined, undefined, undefined, formatDate);
                            viewProps.clear();
                        }}
                    />
                )}
                <ICONS.BUTTON.CLOCK
                    className='text-brand-muted hover:text-foreground-default cursor-pointer'
                    onClick={() => viewProps.changeShowModal(!viewProps.showModal)}
                />
            </div>
            {viewProps.showModal === true && (
                <div className='absolute flex w-fit flex-col'>
                    <div className='bg-background-surface outline-border-default inline-flex flex-col items-start justify-start gap-4 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                        <DurationInputSelect
                            selectItems={['At least', 'At most']}
                            onChange={(newValue) => {
                                viewProps.setLocalPeriod(newValue);
                            }}
                            value={viewProps.localTimePeriod}
                        />
                        <NumberInput
                            min={0}
                            value={viewProps.localDuration}
                            onChange={(newValue) => {
                                viewProps.setLocalDuration(newValue);
                            }}
                            placeholder='Duration'
                            inputClassName='self-stretch h-10 px-4 bg-background-surface rounded-xl outline outline-1 outline-offset-[-1px] outline-border-default text-sm font-medium'
                        />
                        <DurationInputSelect
                            selectItems={['Days', 'Weeks', 'Months', 'Years']}
                            onChange={(newValue) => {
                                viewProps.setLocalUnit(newValue);
                            }}
                            value={viewProps.localTimeUnit}
                        />

                        <button
                            className='bg-brand-primary inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl p-3'
                            disabled={!viewProps.isFilledIn()}
                            onClick={() => {
                                viewProps.setValues();
                            }}>
                            <div className='text-background-surface justify-start text-sm font-medium'>
                                <FormattedMessage
                                    id='DurationInput.DurationFilterBtn'
                                    description='The message to be displayed on the button to apply the filter'
                                    defaultMessage='Filter results'
                                />
                            </div>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
